import { z } from 'zod'

/**
 * Per-panel config for spoken news readout.
 *
 * Deliberately NOT a reuse of the scanner's `audioSettingsSchema`: alerts pick
 * a sound from a catalog and speak a closed vocabulary of tickers, so they
 * carry `selectedSoundId` / `bufferLimit` semantics that mean nothing here,
 * and they lack the source classes, value gates, and rate this needs.
 *
 * Everything defaults to silent and ungated, so adding the tab changes no
 * existing panel's behaviour until someone turns it on.
 */

/**
 * Voices for the ticker+headline utterance: xAI voice ids.
 *
 * NOT Deepgram Aura names. The companion `/tts` route runs xAI-primary with
 * Deepgram as fallback (see docs/superpowers/specs/2026-08-28-xai-voice-provider-design.md),
 * and `xaiVoice()` in the TS service rewrites ANY `aura`-prefixed name to the
 * xAI default. An Aura list here therefore looked like a working picker while
 * every option collapsed to the same voice.
 *
 * Note the readout is the first caller to pass `voice` at all: the spec records
 * that `companion-speech.ts` only ever sets `language`, which is why nothing
 * caught the mapping earlier.
 *
 * A CONSERVATIVE SUBSET. xAI exposes the full roster at
 * `GET https://api.x.ai/v1/tts/voices`, but no key was available to query it,
 * so this lists only ids confirmed in xAI's docs and in the service's own
 * tests. Unknown ids are passed straight through to xAI by `xaiVoice()` and
 * 404 the utterance, which burns the Deepgram fallback on something that is
 * not an outage, so do not add ids here without checking that endpoint first.
 */
export const NEWS_TTS_VOICES = [
  { value: 'eve', label: 'Eve' },
  { value: 'ara', label: 'Ara' },
  { value: 'rex', label: 'Rex' },
] as const

export const DEFAULT_NEWS_TTS_VOICE = 'rex'

/**
 * Resolve a persisted voice to one this picker can show.
 *
 * Panels saved while the list held Aura names carry values like
 * `aura-2-thalia-en`. The backend already maps those to xAI's default so
 * playback is unaffected, but the picker would otherwise display a dead
 * option. Falling back to the default keeps what is shown honest about what
 * is heard.
 */
export function resolveNewsVoice(voice: string | undefined): string {
  return NEWS_TTS_VOICES.some((v) => v.value === voice) ? (voice as string) : DEFAULT_NEWS_TTS_VOICE
}

/** Speed bounds for client-side `playbackRate`. */
export const NEWS_RATE_MIN = 0.75
export const NEWS_RATE_MAX = 1.75

/**
 * Headlines longer than this are truncated at a word boundary before being
 * spoken. Some wire-service headlines run past 200 characters, which is ~20
 * seconds of audio for one story and guarantees the queue backs up.
 */
export const NEWS_HEADLINE_MAX_CHARS = 140

export const newsAudioSettingsSchema = z.object({
  /**
   * A single ladder of escalating verbosity, and the master switch for ALL
   * audio on this panel. Each rung reveals only the settings it actually uses.
   *
   * - 'off'   nothing, whatever else is configured
   * - 'sound' the chosen sound, nothing spoken
   * - 'ticker' sound (if chosen) then the symbol
   * - 'ticker+headline' sound (if chosen) then symbol and story
   *
   * Sound is a rung rather than a wholly independent toggle so there is ONE
   * way to say "ping only". The earlier shape allowed both `mode: 'off'` with a
   * sound set and a dedicated sound mode, which is two spellings of the same
   * intent and a settings screen that can contradict itself.
   */
  mode: z.enum(['off', 'sound', 'ticker', 'ticker+headline']),
  /**
   * xAI voice id for the ticker+headline utterance. Ticker-only mode ignores
   * it and plays the scanner's alert clip voice instead.
   */
  voice: z.string(),
  volume: z.number().min(0).max(100),
  rate: z.number().min(NEWS_RATE_MIN).max(NEWS_RATE_MAX),
  /**
   * Which classes of story speak. Press releases and SEC filings ONLY: Other
   * News is commentary and aggregator noise, which is not what a spoken feed
   * is for (Chris, 2026-08-27). Other News rows still display, they just never
   * speak, and there is no toggle for them.
   *
   * Classification is by SOURCE NAME via `PRESS_RELEASE_SOURCES`, never the
   * payload's `is_pr` flag: the 2026-08-20 sources design records `is_pr` as
   * unreliable, which is why the whole product splits PR from Other News on
   * `news.source`. Using the flag here would make the readout disagree with
   * what the panel displays.
   */
  sources: z.object({
    pressReleases: z.boolean(),
    secFilings: z.boolean(),
  }),
  /**
   * WHICH sound to play. Whether it plays at all is `mode`'s business.
   *
   * Plays before anything is spoken in every rung above 'off'. The scanner's
   * alert settings force a sound-OR-speech choice, but that is a property of
   * its settings model, not of the audio layer: use-alert-effects already
   * fires both branches in one handler, so speech rungs can carry a sound too.
   */
  soundId: z.string().nullable(),
  /** Per-ticker silence window, so one symbol's news burst speaks once. */
  cooldownSec: z.number().min(0).max(3600),
  /** Queue depth. Beyond this the OLDEST pending readout is dropped. */
  bufferLimit: z.number().int().min(1).max(10),
})

/**
 * Note the absence of value gates (min price / volume / move / skip-halted).
 * They were built and then removed on 2026-08-27: the panel already has a
 * filter profile, and a story that does not clear the panel's filters never
 * reaches the readout in the first place. A second, weaker filter language
 * living in the Audio tab could only disagree with it.
 */
export type NewsAudioSettings = z.infer<typeof newsAudioSettingsSchema>

export const DEFAULT_NEWS_AUDIO_SETTINGS: NewsAudioSettings = {
  mode: 'off',
  voice: DEFAULT_NEWS_TTS_VOICE,
  volume: 60,
  rate: 1.5,
  sources: { pressReleases: true, secFilings: false },
  soundId: null,
  cooldownSec: 60,
  bufferLimit: 3,
}
